// src/tenantAdmin/components/CompanyAdminsBlock.jsx
//
// The company's own administrators, as they appear in a workspace's user list.
//
// ⛔⛔ READ-ONLY, with no controls at all — not greyed ones. These rows carry
// `via: "customer"`: the person administers this workspace through the COMPANY and
// owns no membership row in it, so every per-user workspace route answers 404 on
// them (the envelope says as much with `removable: false`). A "Rimuovi" that can
// only fail is worse than no button, so the hint names where they ARE managed
// (the company-admins section of this same tab, for a company admin) instead.
//
// ⛔ Rendered ONCE per list, not once per page: the backend appends these rows to
// EVERY page, unpaginated, uncounted in `total`, and ignores the `email` filter for
// them — so the caller shows this block on page 1 with no search only, or the same
// people would reappear under every page number and survive a search that excludes
// them.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

export default function CompanyAdminsBlock({ rows }) {
  const { t } = useTranslation('admin');
  if (!rows || rows.length === 0) return null;

  return (
    <div style={{
      margin: '16px 0 0', padding: '16px 24px',
      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
      borderRadius: RADII.section, fontFamily: FONT_SANS,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <ShieldCheck size={16} color={COLORS.textSecondary} strokeWidth={1.8} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: COLORS.ink }}>
          {t('manage.companyAdminsTitle')}
        </span>
      </div>
      <div style={{ marginTop: '4px', fontSize: '11px', color: COLORS.textTertiary, lineHeight: 1.5 }}>
        {t('manage.companyAdminsHint')}
      </div>
      <ul style={{ margin: '12px 0 0', padding: 0, listStyle: 'none' }}>
        {rows.map(row => (
          <li
            key={row.user_id}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
              padding: '8px 0', borderTop: `1px solid ${COLORS.border}`,
              fontSize: '13px', color: COLORS.ink,
            }}
          >
            <span>{[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}</span>
            <span style={{ color: COLORS.textSecondary }}>{row.email}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
